import axios, { AxiosInstance, AxiosResponse, CancelToken, CancelTokenSource } from "axios";
import { isUndefined, omitBy } from "lodash-es";
import querystring from "querystring";
import { from, Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Person } from "@/services/DTOs/Person";
import { IAggregatedSearchResult } from "@/services/DTOs/search/AggregatedSearchResult";
import { IConcreteFilterEval, isAndFilterEval, isConcreteFilterEval } from "@/services/DTOs/search/FilterEval";
import { IPageableSearch } from "@/services/DTOs/search/PageableSearch";
import {
  IOpportunitiesSearchCommand,
  IPeopleEvalCommand,
  IPeopleSearchCommand
} from "@/services/DTOs/search/SearchCommand";
import {
  IOpportunitiesEvalResult,
  IOpportunitiesSearchResult,
  IPeopleEvalResult,
  IPeopleSearchResult
} from "@/services/DTOs/search/SearchResult";
import { trackAPICall } from "@/services/SnowplowService";
import { mapError } from "@/utils/ObservableUtils";
import { IArdaPageableSearch } from "./DTOs/arda/IArdaPageableSearch";
import { IArdaProxyPeopleSearchResult } from "./DTOs/arda/IArdaPeopleSearchResult";

export interface ISearchQueryString {
  aggregate?: boolean;
  currency?: string;
  lang: string;
  meta?: string;
  periodicity?: string;
  size?: number;
  after?: string;
  before?: string;
}

interface ISearchQueryStringSuggestions {
  query: string;
  offset: number;
  limit: number;
}

class PostRequestBuilder {

  private client: AxiosInstance;
  private path: string;

  private body?: object;
  private cancelToken?: CancelToken;
  private qs?: string;
  private userGgId?: string;

  constructor(client: AxiosInstance, path: string) {
    this.client = client;
    this.path = path;
  }

  public build(): Observable<AxiosResponse> {
    const authHeaders = this.userGgId ? { "X-Torre-GgId": this.userGgId } : {};
    const request = this.client.post(
      [this.path, this.qs].join("?"),
      this.body || {},
      { cancelToken: this.cancelToken, headers: authHeaders }
    );

    return from(request).pipe(mapError());
  }

  public setBody(body: IPeopleSearchCommand | IPeopleEvalCommand | IOpportunitiesSearchCommand[]): PostRequestBuilder {
    this.body = body;
    return this;
  }

  public setCancelToken(cancelToken: CancelToken): PostRequestBuilder {
    this.cancelToken = cancelToken;
    return this;
  }

  public setQueryString(qs: ISearchQueryString): PostRequestBuilder {
    this.qs = querystring.encode(omitBy(qs, isUndefined));
    return this;
  }

  public setUser(user?: Person): PostRequestBuilder {
    this.userGgId = user ? user.ggId : undefined;
    return this;
  }
}

class GetRequestBuilder {
  private client: AxiosInstance;
  private path: string;

  private qs?: string;
  private cancelToken?: CancelToken;

  constructor(client: AxiosInstance, path: string) {
    this.client = client;
    this.path = path;
  }

  public build(): Observable<AxiosResponse> {
    const request = this.client.get(
      [this.path, this.qs].join("?"),
      { cancelToken: this.cancelToken }
    );

    return from(request).pipe(mapError());
  }

  public setQueryString(qs: ISearchQueryStringSuggestions): GetRequestBuilder {
    this.qs = querystring.encode(omitBy(qs, isUndefined));
    return this;
  }

  public setCancelToken(cancelToken: CancelToken): GetRequestBuilder {
    this.cancelToken = cancelToken;
    return this;
  }
}

class Service {

  private cancelTokens = new Map<string, CancelTokenSource>();
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({ baseURL: `${process.env.VUE_APP_DISCOVERY_SEARCH_BASE_URL}` });
  }

  @trackAPICall("POST", "searchOpportunities", "query", (args) => ({ query: args[0], queryString: args[2] }))
  public searchOpportunities(
    query: IOpportunitiesSearchCommand,
    queryString: ISearchQueryString,
    user?: Person
  ): Observable<IAggregatedSearchResult<IOpportunitiesSearchResult>> {
    return new PostRequestBuilder(this.client, "/opportunities/_search")
      .setBody(query)
      .setQueryString(queryString)
      .setUser(user)
      .build()
      .pipe(
        map((response) => response.data as IAggregatedSearchResult<IOpportunitiesSearchResult>)
      );
  }

  @trackAPICall("POST", "Search", "query", (args) => ({ query: args[0], queryString: args[2] }))
  public searchPeople(
    query: IPeopleSearchCommand,
    queryString: ISearchQueryString,
    user?: Person
  ): Observable<IAggregatedSearchResult<IPeopleSearchResult>> {
    const baseUrl = "/people/_search";

    this.cancelRequestByBaseUrl(baseUrl);

    return new PostRequestBuilder(this.client, baseUrl)
      .setBody(query)
      .setCancelToken(this.cancelTokenFor(baseUrl))
      .setQueryString(queryString)
      .setUser(user)
      .build()
      .pipe(
        map((response) => response.data as IAggregatedSearchResult<IPeopleSearchResult>)
      );
  }

  public suggestionsByName(
    query: string,
    offset: number = 0,
    limit: number = 3
  ): Observable<IArdaPageableSearch<IArdaProxyPeopleSearchResult>> {
    const baseUrl = `${process.env.VUE_APP_DISCOVERY_UI_SELF_URL}/api/suite/people/name-suggestions`;

    this.cancelRequestByBaseUrl(baseUrl);

    const queryObject = {
      query,
      offset,
      limit
    };

    return new GetRequestBuilder(this.client, baseUrl)
      .setQueryString(queryObject)
      .setCancelToken(this.cancelTokenFor(baseUrl))
      .build()
      .pipe(
        map((response) => response.data as IArdaPageableSearch<IArdaProxyPeopleSearchResult>)
      );
  }

  public evaluateOpportunity(
    opportunityId: string,
    queries: IOpportunitiesSearchCommand[],
    user?: Person
  ): Observable<Array<IPageableSearch<IOpportunitiesEvalResult>>> {
    return new PostRequestBuilder(this.client, `/opportunities/${opportunityId}/_eval/`)
      .setBody(queries)
      .setUser(user)
      .build()
      .pipe(
        map((response) => response.data as Array<IPageableSearch<IOpportunitiesEvalResult>>)
      );
  }

  public evaluatePeople(command: IPeopleEvalCommand, user?: Person): Observable<IPageableSearch<IPeopleEvalResult>> {
    return new PostRequestBuilder(this.client, "/people/_eval/")
      .setBody(command)
      .setUser(user)
      .build()
      .pipe(
        map((response) => response.data as IPageableSearch<IPeopleEvalResult>)
      );
  }

  public evaluateCompensationMatch(user: Person, opportunityId: string): Observable<boolean> {
    const payload = {
      ggIds: [user.ggId!],
      query: {
        job: { id: opportunityId }
      }
    };

    return this.evaluatePeople(payload)
      .pipe(
        map((evaluation) => {
          if (evaluation.results.length) {
            const personEval = evaluation.results[0]._meta;
            if (!!personEval.filter && isAndFilterEval(personEval.filter)) {
              const compensationEval = personEval.filter.and.find((e) =>
                isConcreteFilterEval(e) && e.id === "compensation") as IConcreteFilterEval | undefined;
              return !!compensationEval && compensationEval.pass;
            } else {
              return false;
            }
          } else {
            return false;
          }
        })
      );
  }

  private cancelRequestByBaseUrl(baseUrl: string): void {
    if (this.cancelTokens.has(baseUrl)) {
      // @ts-ignore
      this.cancelTokens.get(baseUrl).cancel();
    }
  }

  private cancelTokenFor(baseUrl: string): CancelToken {
    this.cancelTokens.set(baseUrl, axios.CancelToken.source());
    // @ts-ignore
    return this.cancelTokens.get(baseUrl).token;
  }

}

export const SearchService = new Service();
